import PropTypes from 'prop-types';
import React from 'react';
import { Statistics, Numbers, Tooltip } from '@weezevent/nacre';
import i18n from 'i18next';

import { useTranslation } from 'react-i18next';
import { Loader } from 'semantic-ui-react';
import StatisticsTable from './components/statisticsTable';
import Styles from '../../../styles/event/dashboard/eventDashboard.module.css';
import StatisticsVariation from './components/statisticsVariation';
import { StatisticsMoreLabel } from './statisticsAttendees';

export default function StatisticsRevenues({ data = null, currency = 'EUR', titleKey = 'sum_revenue', prefixUrl, eventStatus, eventEndDate }) {
    const { t } = useTranslation();
    const locale = i18n.language;

    const statistics = React.useMemo(() => {
        if (!data) {
            return null;
        }
        let tmpAttendeesStats = data.find(groupStats => groupStats.name === 'attendees_stats').value;
        let tmpRevenuesStats = data.find(groupStats => groupStats.name === 'revenues_stats').value;
        return { ...tmpAttendeesStats, ...tmpRevenuesStats };
    }, [data]);

    // Codes promo
    if (!statistics) {
        return (
            <Statistics stat="">
                <Loader active inline="centered" style={{ marginBottom: '50px' }} />
            </Statistics>
        );
    }

    const rowsTableTop = {
        sum_revenue_weez: {
            title: (
                <span>
                    <Tooltip margin={false} position={'bottom'} triggerOnHover title={t('ticket.eventDashboard.statisticsRevenues.sumRevenueWeezHelp')}>
                        {t('ticket.eventDashboard.statisticsRevenues.sumRevenueWeez')}
                    </Tooltip>
                </span>
            ),
            value: (
                <Statistics.CellStyle bold>
                    <Numbers value={statistics.sum_revenue_weez_web} locale={locale} currency={currency} showNullDecimal />
                </Statistics.CellStyle>
            )
        },
        sum_revenue_weez_ticket_office: {
            title: <span>{t('ticket.eventDashboard.statisticsRevenues.sumRevenueWeezTicketOffice')}</span>,
            value: (
                <Statistics.CellStyle bold>
                    <Numbers value={statistics.sum_revenue_weez_ticket_office} locale={locale} currency={currency} showNullDecimal />
                </Statistics.CellStyle>
            )
        },
        sum_revenue_weez_offline: {
            title: (
                <span>
                    <Tooltip margin={false} position={'bottom'} triggerOnHover title={t('ticket.eventDashboard.statisticsRevenues.sumRevenueWeezOfflineHelp')}>
                        {t('ticket.eventDashboard.statisticsRevenues.sumRevenueWeezOffline')}
                    </Tooltip>
                </span>
            ),
            value: (
                <Statistics.CellStyle bold>
                    <Numbers value={statistics.sum_revenue_weez_offline} locale={locale} currency={currency} showNullDecimal />
                </Statistics.CellStyle>
            )
        },
        sum_revenue_weez_partners: {
            title: <span>{t('ticket.eventDashboard.statisticsRevenues.sumRevenueWeezPartners')}</span>,
            value: (
                <Statistics.CellStyle bold>
                    <Numbers value={statistics.sum_revenue_weez_partners} locale={locale} currency={currency} showNullDecimal />
                </Statistics.CellStyle>
            )
        },
        sum_revenue_non_weez: {
            title: (
                <span>
                    <Tooltip margin={false} position={'bottom'} triggerOnHover title={t('ticket.eventDashboard.statisticsRevenues.sumRevenueNoWeezHelp')}>
                        {t('ticket.eventDashboard.statisticsRevenues.sumRevenueNoWeez')}
                    </Tooltip>
                </span>
            ),
            value: (
                <Statistics.CellStyle bold>
                    <Numbers value={statistics.sum_revenue_non_weez} locale={locale} currency={currency} showNullDecimal />
                </Statistics.CellStyle>
            )
        },
        sum_upsellings: {
            title: t('ticket.eventDashboard.statisticsRevenues.sumRevenueCrossselling'),
            value: (
                <Statistics.CellStyle bold>
                    <Numbers value={statistics.sum_upsellings} locale={locale} currency={currency} showNullDecimal />
                </Statistics.CellStyle>
            )
        },
        sum_revenue_deleted: {
            title: t('ticket.eventDashboard.statisticsRevenues.sumRevenueDeleted'),
            value: (
                <Statistics.CellStyle bold>
                    {`+ `}
                    <Numbers value={statistics.sum_revenue_deleted} locale={locale} currency={currency} showNullDecimal />
                </Statistics.CellStyle>
            )
        },
        sum_refunds: {
            title: t('ticket.eventDashboard.statisticsRevenues.sumRefunds'),
            value: (
                <Statistics.CellStyle bold>
                    {`- `}
                    <Numbers value={statistics.sum_refunds} locale={locale} currency={currency} showNullDecimal />
                </Statistics.CellStyle>
            )
        }
    };

    const rowsTableBottom = {
        sum_revenue_reservation: {
            title: <Statistics.CellStyle grey>{t('ticket.eventDashboard.statisticsRevenues.sumRevenueReservation')}</Statistics.CellStyle>,
            value: (
                <Statistics.CellStyle bold grey>
                    {`+ `}
                    <Numbers value={statistics.sum_revenue_reservation} locale={locale} currency={currency} showNullDecimal />
                </Statistics.CellStyle>
            )
        },
        sum_revenue_in_progress: {
            title: <Statistics.CellStyle grey>{t('ticket.eventDashboard.statisticsRevenues.sumRevenueInProgress')}</Statistics.CellStyle>,
            value: (
                <Statistics.CellStyle bold grey>
                    {`+ `}
                    <Numbers value={statistics.sum_revenue_in_progress} locale={locale} currency={currency} showNullDecimal />
                </Statistics.CellStyle>
            )
        }
    };
    const enabledStats = parseInt(+statistics[titleKey] || 0, 10) + parseInt(statistics.sum_upsellings || 0, 10) > 0;
    return (
        <Statistics
            helpLabel={
                parseInt(+statistics.sum_revenue_deleted, 10) > 0 ? (
                    <span>
                        {t('ticket.eventDashboard.statisticsRevenues.subtitleHelp', {
                            amount: new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(+statistics.sum_revenue_deleted)
                        })}
                    </span>
                ) : null
            }
            className={enabledStats ? Styles['statistics-revenues'] : Styles['statistics-revenues-disable']}
            subtitle={t('ticket.eventDashboard.statisticsRevenues.subtitle')}
            stat={
                <span style={{ color: +parseInt(+statistics[titleKey] || 0, 10) + parseInt(statistics.sum_upsellings || 0, 10) > 0 ? 'rgb(var(--primary-color))' : '#878c9b' }}>
                    <Numbers value={+statistics[titleKey]} locale={locale} currency={currency} showNullDecimal />
                </span>
            }
            moreURL={`${prefixUrl}/attendees/stats`}
            moreLabel={
                parseInt(+statistics[titleKey] || 0, 10) + parseInt(statistics.sum_upsellings || 0, 10) > 0 ? (
                    <StatisticsMoreLabel title={t('ticket.eventDashboard.statisticsRevenues.link')} />
                ) : null
            } //TODO
            footer={
                parseInt(+statistics[titleKey] || 0, 10) + parseInt(statistics.sum_upsellings || 0, 10) > 0 && (
                    <StatisticsVariation
                        total={statistics[titleKey]}
                        variationUp={statistics.variation_last_week_up}
                        variationDown={statistics.variation_last_week_down}
                        variationUnit={'%'}
                        variationLabel={t('ticket.eventDashboard.statisticsRevenues.variationLabel')}
                        eventStatus={eventStatus}
                        eventEndDate={eventEndDate}
                    />
                )
            }
        >
            {parseInt(+statistics[titleKey] || 0, 10) + parseInt(statistics.sum_upsellings || 0, 10) > 0 && (
                <Statistics.Content>
                    <StatisticsTable statistics={statistics} rows={rowsTableTop} />
                    <StatisticsTable statistics={statistics} rows={rowsTableBottom} className={Styles['statistics-table-bottom']} />
                </Statistics.Content>
            )}
        </Statistics>
    );
}

StatisticsRevenues.propTypes = {
    data: PropTypes.array,
    currency: PropTypes.string,
    titleKey: PropTypes.string,
    prefixUrl: PropTypes.string.isRequired,
    eventStatus: PropTypes.string.isRequired,
    eventEndDate: PropTypes.string
};
